/**
 * ContextSelector Component with Y2K Glassmorphism
 *
 * Four-button selector for conversation context (Family, Work, Friends, Dating).
 * Features:
 * - Single selection (radio group pattern)
 * - Glass pill indicator with blur + gradient + glow (Y2K Phase 4)
 * - Animated sliding (200ms, Reanimated 3.16.7)
 * - Haptic feedback on selection (expo-haptics)
 * - Accessibility compliant (WCAG 2.1 AA)
 * - Per-context gradients (Family: Mint→Teal, Work: Blue, Friends: Lavender→Purple, Dating: Pink)
 * - Reduced motion support
 * - Tier-adaptive border radius (Layer 1: 12px, Layer 2: 8px, Layer 3: 4px)
 * - Mobile-optimized: Responsive text (14px mobile, 16px desktop) + flex layout (all 4 pills visible)
 *
 * Updated: 2025-02-04 (Mobile optimization: removed ScrollView, added flex-1 + reduced padding)
 */
import {
  View,
  Text,
  Pressable,
  AccessibilityInfo,
  StyleSheet,
  Platform,
} from "react-native";
import { useState, useEffect } from "react";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSpring,
} from "react-native-reanimated";
import { BlurView } from "expo-blur";
import { LinearGradient } from "expo-linear-gradient";
import { useTranslation } from "@dramadetector/shared";
import { captureEvent } from "../lib/analytics";
import { triggerSelection } from "../lib/haptics";

// Type definitions
type ContextType = "family" | "work" | "friends" | "dating";

interface ContextSelectorProps {
  value: ContextType;
  onChange: (context: ContextType) => void;
  disabled?: boolean;
}

// Y2K gradient configurations (70% opacity for accessibility on dark background)
const CONTEXT_GRADIENTS = {
  family: ['rgba(16, 185, 129, 0.7)', 'rgba(6, 182, 212, 0.7)'] as const, // Mint → Teal
  work: ['rgba(59, 130, 246, 0.7)', 'rgba(37, 99, 235, 0.7)'] as const, // Blue gradient
  friends: ['rgba(192, 132, 252, 0.7)', 'rgba(168, 85, 247, 0.7)'] as const, // Lavender → Purple
  dating: ['rgba(236, 72, 153, 0.7)', 'rgba(219, 39, 119, 0.7)'] as const, // Pink gradient
} as const;

const CONTEXT_GLOW_COLORS = {
  family: '#06b6d4', // Teal glow
  work: '#3b82f6', // Blue glow
  friends: '#a855f7', // Purple glow
  dating: '#ec4899', // Pink glow
} as const;

// Y2K Spring Animation Config (matches GlassTabIcon)
const Y2K_SPRING_CONFIG = {
  damping: 15,
  stiffness: 150,
  mass: 1,
  overshootClamping: false,
  restDisplacementThreshold: 0.01,
  restSpeedThreshold: 0.01,
};

/**
 * Get the index of a context
 */
function getContextIndex(context: ContextType): number {
  const contexts: ContextType[] = ["family", "work", "friends", "dating"];
  return contexts.indexOf(context);
}

export function ContextSelector({
  value,
  onChange,
  disabled = false,
}: ContextSelectorProps) {
  const { t } = useTranslation('home');
  const [isReduceMotionEnabled, setReduceMotionEnabled] = useState(false);
  const [initialValue] = useState(value); // Track initial value to detect user changes

  // Context options with translated labels
  const CONTEXT_OPTIONS = [
    {
      value: "family" as ContextType,
      label: t('context_selector.options.family'),
      gradient: CONTEXT_GRADIENTS.family,
      glowColor: CONTEXT_GLOW_COLORS.family,
      accessibilityLabel: t('context_selector.accessibility.select_family'),
    },
    {
      value: "work" as ContextType,
      label: t('context_selector.options.work'),
      gradient: CONTEXT_GRADIENTS.work,
      glowColor: CONTEXT_GLOW_COLORS.work,
      accessibilityLabel: t('context_selector.accessibility.select_work'),
    },
    {
      value: "friends" as ContextType,
      label: t('context_selector.options.friends'),
      gradient: CONTEXT_GRADIENTS.friends,
      glowColor: CONTEXT_GLOW_COLORS.friends,
      accessibilityLabel: t('context_selector.accessibility.select_friends'),
    },
    {
      value: "dating" as ContextType,
      label: t('context_selector.options.dating'),
      gradient: CONTEXT_GRADIENTS.dating,
      glowColor: CONTEXT_GLOW_COLORS.dating,
      accessibilityLabel: t('context_selector.accessibility.select_dating'),
    },
  ];

  // 1. Reduced Motion Support: Check accessibility settings on mount and listen for changes.
  useEffect(() => {
    const checkReduceMotion = async () => {
      setReduceMotionEnabled(await AccessibilityInfo.isReduceMotionEnabled());
    };

    checkReduceMotion();

    const subscription = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      (enabled) => {
        setReduceMotionEnabled(enabled);
      },
    );

    return () => {
      subscription.remove();
    };
  }, []);

  // 2. Animated Indicator Position & Glow
  const indicatorPosition = useSharedValue(getContextIndex(value));
  const glowOpacity = useSharedValue(0.6);

  // Calculate indicator position with spring animation
  useEffect(() => {
    const index = getContextIndex(value);

    if (isReduceMotionEnabled) {
      // Instant transition for reduced motion
      indicatorPosition.value = withTiming(index, { duration: 0 });
      glowOpacity.value = 0.6; // Static glow (no animation)
    } else {
      // Y2K spring animation with playful bounce
      indicatorPosition.value = withSpring(index, Y2K_SPRING_CONFIG);

      // Subtle glow pulse on selection
      glowOpacity.value = withSpring(0.8, { ...Y2K_SPRING_CONFIG, damping: 20 });
      setTimeout(() => {
        glowOpacity.value = withSpring(0.6, Y2K_SPRING_CONFIG);
      }, 100);
    }
  }, [value, isReduceMotionEnabled]);

  const animatedIndicatorStyle = useAnimatedStyle(() => {
    const translateXPercent = indicatorPosition.value * 100;

    // Platform-specific shadow handling to avoid web deprecation warnings
    if (Platform.OS === 'web') {
      return {
        transform: [{ translateX: `${translateXPercent}%` }],
        width: '25%',
        // Web uses boxShadow which is set via inline style below
      };
    }

    return {
      transform: [{ translateX: `${translateXPercent}%` }],
      width: '25%',
      shadowOpacity: glowOpacity.value,
    };
  });

  // 3. Handle Selection with Haptics
  const handleSelect = (context: ContextType) => {
    if (disabled || context === value) return;

    // Trigger haptic feedback (web-safe)
    triggerSelection();

    // Track context selection (NEW - Phase 1.5)
    captureEvent('context_selected', {
      context,
      is_default: context === initialValue,
    });

    // Call onChange callback
    onChange(context);
  };

  // Get current context gradient for glass pill
  const currentGradient = CONTEXT_GRADIENTS[value];
  const currentGlowColor = CONTEXT_GLOW_COLORS[value];

  return (
    <View
      className="w-full max-w-md mx-auto"
      accessibilityRole="radiogroup"
      accessibilityLabel={t('context_selector.accessibility.label')}
    >
      <View
        className="relative rounded-tier p-1 flex-row"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }} // DD-261: 5% white glass (matches prototype inactive buttons)
      >
        {/* Y2K Glass Pill Indicator - 4-layer stack */}
        <Animated.View
          style={[
            animatedIndicatorStyle,
            {
              position: 'absolute',
              left: 4,
              top: 4,
              bottom: 4,
              overflow: 'hidden',
              // Platform-specific shadow styles
              ...(Platform.OS === 'web'
                ? { boxShadow: `0px 0px 8px ${currentGlowColor}` }
                : {
                    shadowColor: currentGlowColor,
                    shadowOffset: { width: 0, height: 0 },
                    shadowRadius: 8,
                  }),
            } as any,
          ]}
          className="rounded-tier"
          pointerEvents="none"
        >
          {/* Layer 1: Solid dark base */}
          <View style={[StyleSheet.absoluteFill, { backgroundColor: '#111827' }]} />

          {/* Layer 2: BlurView for frosted glass */}
          <BlurView intensity={60} tint="dark" style={StyleSheet.absoluteFill} />

          {/* Layer 3: Solid teal background (DD-261: HTML prototype) */}
          <View style={[StyleSheet.absoluteFill, { backgroundColor: '#14B8A6' }]} />

          {/* Layer 4: Subtle chrome border */}
          <View
            style={[
              StyleSheet.absoluteFill,
              {
                borderWidth: 1,
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 8, // Matches rounded-tier
              },
            ]}
          />
        </Animated.View>

        {/* Context Buttons */}
        {CONTEXT_OPTIONS.map((option) => {
          const isActive = value === option.value;

          return (
            <Pressable
              key={option.value}
              onPress={() => handleSelect(option.value)}
              disabled={disabled}
              className={`
                items-center justify-center rounded-tier
                min-h-[44px] sm:min-h-[48px] md:min-h-[52px]
                flex-1 px-2
                ${disabled ? "opacity-50" : ""}
              `}
              accessibilityRole="radio"
              accessibilityState={{ checked: isActive, disabled }}
              accessibilityLabel={option.accessibilityLabel}
            >
              <Text
                className={`
                  text-sm sm:text-base font-semibold
                  ${isActive ? "text-white" : "text-gray-400"}
                `}
              >
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}
